import React, { forwardRef, useEffect, useMemo } from "react";
import { withTheme } from "styled-components/macro";

import { Chart, Bar, Scatter } from "react-chartjs-2";
import "chartjs-adapter-moment";
import zoomPlugin from "chartjs-plugin-zoom";
import Loader from "../Loader";
import { Typography } from "@material-ui/core";
import { lineColors } from "../../utils";
import { add } from "date-fns";
import annotationPlugin from "chartjs-plugin-annotation";

Chart.register(zoomPlugin);
Chart.register(annotationPlugin);

// Stable default so the options memo below doesn't invalidate every render.
const EMPTY_ANNOTATIONS = {};

const TimeseriesLineChartDataViz = forwardRef(
  (
    {
      data,
      error,
      isLoading,
      filterValues = "",
      yLLabel,
      type = "bar",
      yLReverse = false,
      reverseLegend = true,
      xLabelUnit = "day",
      xLabelFormat = "MMM YYYY",
      tooltipFormat = "MM-DD-YYYY",
      yRLLabel = null,
      theme,
      displayLegend = true,
      setIsGraphRefCurrent = () => {},
      stacked = false,
      maxTicksX = 8.3,
      maxTicksYL = 11,
      maxTicksYR = 11,
      align = "center",
      enableLegendClick = false,
      annotatedLines = EMPTY_ANNOTATIONS,
      beginAtZero = false,
    },
    ref
  ) => {
    useEffect(() => {
      setIsGraphRefCurrent(true);
    }, []); //eslint-disable-line

    // Plugins reference only module-level constants and read live chart state at
    // draw time, so they never need to be recreated.
    const plugins = useMemo(
      () => [
        {
          id: "chartFillBackground",
          beforeDraw: (chart) => {
            const ctx = chart.ctx;
            ctx.fillStyle = lineColors.lightGray;
            ctx.fillRect(0, 0, chart.width, chart.height);
          },
        },
        {
          id: "chartAreaBorder",
          beforeDraw(chart) {
            const {
              ctx,
              chartArea: { left, top, width, height },
            } = chart;
            if (chart.options.plugins.zoom.zoom.wheel.enabled) {
              ctx.save();
              ctx.strokeStyle = lineColors.maroon;
              ctx.lineWidth = 3;
              ctx.strokeRect(left, top, width, height);
              ctx.restore();
            }
          },
        },
      ],
      []
    );

    // Memoized so unrelated re-renders (map interactions, height toggles, etc.)
    // don't hand react-chartjs-2 a new options object and force a full
    // chart.update() over the entire dataset. Animation is disabled because
    // animating large time series is a major source of jank.
    const options = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
          intersect: false,
          mode: "index",
        },
        plugins: {
          annotation: {
            ...{ drawTime: "afterDatasetsDraw" },
            ...annotatedLines,
          },
          filler: {
            propagate: false,
          },
          tooltip: {
            itemSort: function (a, b) {
              return b.datasetIndex - a.datasetIndex;
            },
            callbacks: {
              footer: (tooltipItems) => {
                return (
                  tooltipItems[0].dataset.popupInfo &&
                  `Source: ${
                    tooltipItems[0].dataset.popupInfo[tooltipItems[0].dataIndex]
                  }`
                );
              },
              label: function (tooltipItems) {
                return ` ${tooltipItems.dataset.label}: ${tooltipItems.formattedValue} ${tooltipItems.dataset.units}`;
              },
            },
            footerAlign: "center",
            //TODO
            // footerColor: ctx =>
          },
          legend: {
            display: displayLegend,
            reverse: reverseLegend,
            ...(!enableLegendClick && {
              onClick: () =>
                // e, legendItem, legend
                {
                  return null;
                  // const currentIndex = legendItem.datasetIndex;
                  // ref.current.setDatasetVisibility(
                  //   currentIndex,
                  //   !ref.current.isDatasetVisible(currentIndex)
                  // );
                },
            }),
            labels: {
              ...(!enableLegendClick && {
                filter: (legendItem) => {
                  return !legendItem.hidden;
                },
              }),
              usePointStyle: true,
              color: lineColors.darkGray,
            },
          },
          zoom: {
            pan: {
              enabled: true,
              mode: "xy",
            },
            zoom: {
              mode: "x",
              scaleMode: "xy",
              wheel: {
                enabled: false,
              },
              pinch: {
                enabled: true,
              },
            },
            //TODO line segment styling
          },
        },

        scales: {
          x: {
            type: "time",
            offset: false,
            min:
              filterValues.previousDays === ""
                ? null
                : filterValues.checked
                ? add(new Date().getTime(), {
                    days: -filterValues.previousDays,
                  })
                : filterValues.startDate,
            max:
              filterValues.previousDays === ""
                ? null
                : filterValues.checked
                ? new Date()
                : filterValues.endDate,
            time: {
              unit: xLabelUnit,
              displayFormats: {
                [xLabelUnit]: xLabelFormat,
              },
              tooltipFormat: tooltipFormat,
            },
            grid: {
              display: false,
            },
            ticks: {
              color: lineColors.darkGray,
              maxTicksLimit: maxTicksX,
              source: data.labels?.length === 1 ? "labels" : "auto",
              align: align,
            },
            stacked: stacked,
          },
          yL: {
            beginAtZero: beginAtZero,
            position: "left",
            reverse: yLReverse,
            display: true,
            title: {
              display: true,
              text: yLLabel,
              color: lineColors.darkGray,
            },
            ticks: {
              color: lineColors.darkGray,
              maxTicksLimit: maxTicksYL,
            },
            grid: {
              color: theme.palette.text.gridLines,
              borderDash: [5, 5],
              drawBorder: true,
              drawTicks: true,
            },
            stacked: stacked,
          },
          yR: {
            position: "right",
            display: !!yRLLabel,
            title: {
              display: true,
              text: yRLLabel,
              color: lineColors.darkGray,
            },
            ticks: {
              color: lineColors.darkGray,
              maxTicksLimit: maxTicksYR,
            },
            grid: {
              display: false,
              drawTicks: true,
              drawBorder: false,
            },
          },
        },
        onClick(e) {
          const chart = e.chart;
          chart.options.plugins.zoom.zoom.wheel.enabled =
            !chart.options.plugins.zoom.zoom.wheel.enabled;
          chart.update();
        },
      }),
      [
        data,
        filterValues,
        theme,
        yLLabel,
        yRLLabel,
        displayLegend,
        reverseLegend,
        enableLegendClick,
        xLabelUnit,
        xLabelFormat,
        tooltipFormat,
        maxTicksX,
        maxTicksYL,
        maxTicksYR,
        align,
        stacked,
        beginAtZero,
        yLReverse,
        annotatedLines,
      ]
    );

    if (error) return "An error has occurred: " + error.message;
    return (
      <>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {data?.datasets?.length > 0 ? (
              type === "scatter" ? (
                <Scatter
                  plugins={plugins}
                  ref={ref}
                  data={data}
                  options={options}
                  type={type}
                />
              ) : (
                <Bar
                  plugins={plugins}
                  ref={ref}
                  data={data}
                  options={options}
                  type={type}
                />
              )
            ) : (
              <Typography>No Data Available</Typography>
            )}
          </>
        )}
      </>
    );
  }
);
export default withTheme(TimeseriesLineChartDataViz);
